import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ExternalLink, FileText, LoaderCircle, RefreshCw } from "lucide-react";
import { api, type ClinicalAttachment } from "./api";

export function PatientHealthDocuments({
  organizationId,
  petId,
}: {
  organizationId: string;
  petId: string;
}): ReactNode {
  const [documents, setDocuments] = useState<ClinicalAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((current) => current + 1), []);
  useEffect(() => {
    let alive = true;
    setDocuments([]);
    setLoading(true);
    setError(null);
    void api
      .patientClinicalDocuments(organizationId, petId)
      .then((result) => {
        if (alive) setDocuments(result);
      })
      .catch((reason: unknown) => {
        if (alive)
          setError(
            reason instanceof Error ? reason.message : "No pudimos abrir los documentos clínicos.",
          );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [organizationId, petId, revision]);
  return (
    <section className="panel vet-access-section" aria-labelledby="patient-documents-title">
      <h2 id="patient-documents-title">Documentos de salud</h2>
      <p className="muted">
        Incluye documentos aportados por el tutor y por profesionales, también los que no pertenecen
        a una consulta.
      </p>
      {loading ? (
        <p role="status">
          <LoaderCircle className="spin" size={17} /> Abriendo documentos…
        </p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : documents.length ? (
        <ul className="clinical-document-list">
          {documents.map((document) => (
            <li key={document.id}>
              <FileText size={17} />
              <span>
                <strong>
                  {document.title || document.originalFilename || "Documento clínico"}
                </strong>
                <small>
                  {document.sourceType === "TUTOR_REPORTED"
                    ? "Aportado por tutor"
                    : (document.authorName ?? "Registro clínico")}
                </small>
              </span>
              <a
                className="icon-button"
                href={document.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Abrir ${document.title}`}
              >
                <ExternalLink size={17} />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Sin documentos registrados.</p>
      )}
      <button className="button-link" onClick={reload} disabled={loading}>
        <RefreshCw size={15} /> Actualizar documentos y enlaces privados
      </button>
    </section>
  );
}
